import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import {
  DossierSectionOptions,
  DossierSectionStatusOptions,
} from 'features/captains-form/slices/constants';

import { selectDocuments } from '../../../slices/documentsSlice';
import {
  createPortcallStatusLog,
  getGeneralDeclaration,
  getPortCall,
  getPortcallStatusLog,
  selectGeneralData,
  selectGeneralDeclaration,
  selectPortallStatusLog,
  updateGeneralDeclaration,
  updateMasterSignature,
  updateVesselStamp,
} from '../../../slices/portCallSlice';

import MasterSignature from './MasterSignature';
import DragAndDropPicture from '../../../../forms/components/DragAndDropPicture';
import RadioButtonsCustom from '../../../../forms/components/RadioButtonsCustom';
import Textarea from '../../../../forms/components/TextArea';
import { FormRefContext } from '../../../../global/context/FormRefContext';
import Page from '../../../../layout/components/Page';
import PageHeader from '../../../../layout/components/PageHeader';
import SkipAndNextButtons from '../../port-call-data/components/SkipAndNextButtons';
import { PortInstructionItem } from '../../summary/components/PortInstructionItem';

import '../styles/general-declaration.scss';

const generalDeclarationOptions = [
  {
    key: 'I hereby declare that all information provided is true. *',
    value: true,
  },
];
const readGeneralDeclarationOptions = [
  {
    key: 'I hereby declare that I have read the General Instructions provided above. *',
    value: true,
  },
];

const GeneralDeclaration = () => {
  const formRef = useRef();
  const { formContextRef } = useContext(FormRefContext);
  let navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const generalDeclaration = useSelector(selectGeneralDeclaration);
  const portCallData = useSelector(selectGeneralData);
  const portCallDocuments = useSelector(selectDocuments)?.documents;
  const portCallStatusLog = useSelector(selectPortallStatusLog);
  const instructionsApiDocs = useSelector(
    (state) => state.documents.portInstructions
  );
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false);
  const [signature, setSignature] = useState(null);
  const [vesselStamp, setVesselStamp] = useState(null);

  useEffect(() => {
    dispatch(getGeneralDeclaration(params.uid));
    dispatch(getPortCall(params.uid));
    setVesselStamp(portCallData.vesselStamp);
  }, [dispatch, params.uid, portCallData.vesselStamp]);

  useEffect(() => {
    formContextRef.current = formRef.current;
    if (generalDeclaration?.generalDeclarationAccepted === false)
      setIsSubmitButtonDisabled(true);
  }, [formContextRef, formRef, generalDeclaration, portCallData]);

  const initialValues = {
    generalDeclarationAccepted: generalDeclaration?.generalDeclarationAccepted,
    generalDeclarationRemarks: generalDeclaration?.generalDeclarationRemarks,
    generalDeclarationRead: generalDeclaration?.generalDeclarationRead,
  };

  let instructions = [];
  if (
    portCallDocuments?.instructionsDocuments &&
    portCallDocuments?.instructionsDocuments.length > 0
  ) {
    instructions = portCallDocuments?.instructionsDocuments?.map((_) =>
      _.name ? _ : { ..._, name: 'Instruction Document' }
    );
  }
  if (instructionsApiDocs && instructionsApiDocs.length > 0) {
    instructions = instructions.concat(instructionsApiDocs);
  }

  const validationSchema = Yup.object({
    generalDeclarationAccepted: Yup.boolean()
      .oneOf([true], 'You have to select this option to proceed.')
      .required('You have to select this option to proceed.'),
    generalDeclarationRemarks: Yup.string().nullable(),
    generalDeclarationRead: Yup.boolean().nullable(),
  });

  const handleSubmit = async (values) => {
    if (signature) {
      const isSuccess = await dispatch(
        updateMasterSignature({ image: signature, uid: params.uid })
      ).then((response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      });
      if (!isSuccess) return;
    }

    const isSuccessVesselStamp = await dispatch(
      updateVesselStamp({ image: vesselStamp, uid: params.uid })
    ).then((response) => {
      if (response.type && response.type.endsWith('/rejected')) return false;
      else return true;
    });
    if (!isSuccessVesselStamp) return;

    const isSuccessUpdate = await dispatch(
      updateGeneralDeclaration({
        uid: params.uid,
        generalDeclaration: values,
      })
    ).then((response) => {
      if (response.type && response.type.endsWith('/rejected')) return false;
      else return true;
    });
    if (!isSuccessUpdate) return;

    const isSuccessGet = await dispatch(getPortCall(params.uid)).then(
      (response) => {
        if (response.type && response.type.endsWith('/rejected')) return false;
        else return true;
      }
    );
    if (!isSuccessGet) return;

    const isSuccessUpdateGDDeclareStatus = await dispatch(
      createPortcallStatusLog({
        statusLog: {
          uid: params.uid,
          section: DossierSectionOptions.GeneralDeclarationDeclaration,
          status: DossierSectionStatusOptions.SubmittedToNmap,
        },
      })
    ).then((response) => {
      if (response.type && response.type.endsWith('/rejected')) return false;
      else return true;
    });
    if (!isSuccessUpdateGDDeclareStatus) return;

    const isSuccessUpdateGDStatus = await dispatch(
      createPortcallStatusLog({
        statusLog: {
          uid: params.uid,
          section: DossierSectionOptions.GeneralDeclaration,
          status: DossierSectionStatusOptions.SubmittedToNmap,
        },
      })
    ).then((response) => {
      if (response.type && response.type.endsWith('/rejected')) return false;
      else return true;
    });
    if (!isSuccessUpdateGDStatus) return;

    dispatch(getPortcallStatusLog(params.uid));

    navigate(`/${params.uid}/solas-and-isps-code/questions`);
  };

  const isSubsectionsSubmitted = () => {
    const isPortCallDataSubmitted =
      portCallStatusLog?.find(
        (_) =>
          _?.section === DossierSectionOptions.GeneralDeclarationPortcallData
      )?.status === DossierSectionStatusOptions.SubmittedToNmap;

    const isVesselDataSubmitted =
      portCallStatusLog?.find(
        (_) => _?.section === DossierSectionOptions.GeneralDeclarationVesselData
      )?.status === DossierSectionStatusOptions.SubmittedToNmap;

    const isInspectionDataSubmitted =
      portCallStatusLog?.find(
        (_) =>
          _?.section === DossierSectionOptions.GeneralDeclarationInspections
      )?.status === DossierSectionStatusOptions.SubmittedToNmap;

    const isCargoDataSubmitted =
      portCallStatusLog?.find(
        (_) =>
          _?.section ===
          DossierSectionOptions.GeneralDeclarationCargoInformation
      )?.status === DossierSectionStatusOptions.SubmittedToNmap;

    const isArrivalDangerousGoodsSubmitted =
      portCallStatusLog?.find(
        (_) =>
          _?.section === DossierSectionOptions.ArrivalDangerousGoodsDeclaration
      )?.status === DossierSectionStatusOptions.SubmittedToNmap;

    const isDepartureDangerousGoodsSubmitted =
      portCallStatusLog?.find(
        (_) =>
          _?.section ===
          DossierSectionOptions.DepartureDangerousGoodsDeclaration
      )?.status === DossierSectionStatusOptions.SubmittedToNmap;

    const isSubmitted =
      isPortCallDataSubmitted &&
      isVesselDataSubmitted &&
      isInspectionDataSubmitted &&
      isCargoDataSubmitted &&
      isArrivalDangerousGoodsSubmitted &&
      isDepartureDangerousGoodsSubmitted;
    return isSubmitted;
  };

  return (
    <Page>
      <div className="general-declaration">
        <PageHeader
          title={'General Declaration'}
          subtitle={`Finally, could you please declare that the crew list is true and that all the Certificates and documents required to be on board in compliance with SOLAS 74/78, MARPOL 73/78, Memorandum of Understanding for Port State Control (MOU-PSC), and such other Certificates and documents as may be legally required depending on the type or class of vessel, are on board and in force and their validity extends at least until the date of arrival at the next port of destination?`}
        />
        <Formik
          innerRef={formRef}
          enableReinitialize
          onSubmit={handleSubmit}
          initialValues={initialValues}
          validationSchema={validationSchema}>
          {() => (
            <Form className="general-declaration-form">
              <div className="general-declaration-form__first-row">
                <RadioButtonsCustom
                  name="generalDeclarationAccepted"
                  options={generalDeclarationOptions}
                  onOptionClick={() => {
                    setIsSubmitButtonDisabled(!isSubsectionsSubmitted());
                  }}
                />
              </div>
              <PortInstructionItem
                title={'General Instructions'}
                subtitle={
                  'Read the port call instructions provided by your agent before submitting the form.'
                }
                instructionsText={portCallData?.instructionsText}
                emptyDocumentsMessage={'There are no Port Call Instructions.'}
                documents={instructions}
              />
              <div className="general-declaration-form__second-row">
                <Textarea
                  name="generalDeclarationRemarks"
                  rows={7}
                  placeholder="Remarks (if any)"
                  label="Remarks (if any)"
                />
              </div>
              <div className="general-declaration-form__third-row">
                <RadioButtonsCustom
                  name="generalDeclarationRead"
                  options={readGeneralDeclarationOptions}
                />
              </div>
              <MasterSignature
                portCallData={portCallData}
                title={"Captain's signature *"}
                subtitle={'Draw your signature inside the designated space.'}
                setSignature={setSignature}
              />
              <DragAndDropPicture
                openModalButtonText={'Upload vessel stamp'}
                uploadButtonText={'Upload'}
                acceptedFormats={['.jpeg', '.jpg', '.png']}
                vesselStamp={portCallData.vesselStamp}
                setVesselStamp={setVesselStamp}
              />
              <SkipAndNextButtons
                next={`/${params.uid}/solas-and-isps-code/questions`}
                back={`/${params.uid}/dangerous-goods`}
                submitLabel="Submit"
                submitDisabled={isSubmitButtonDisabled}
                submitArrow={false}
              />
            </Form>
          )}
        </Formik>
      </div>
    </Page>
  );
};

export default GeneralDeclaration;
